import { ArrowLeft, ExternalLink, RefreshCw } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { formatarData, formatarDataHora } from '@/nucleo/formato/datas';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/ui/base/card';
import { Checkbox } from '@/ui/base/checkbox';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/ui/base/table';
import { Textarea } from '@/ui/base/textarea';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { Spinner } from '@/ui/Spinner';

import { mensagemDoErro } from '../parametrizacao/cadastros.dados';
import { ComoFuncionaAIntegracao } from './ComoFuncionaAIntegracao';
import type { FonteDoPainel } from './contrato';
import { useAlterarRecorte, useAtualizarAgora, usePainelDaSincronizacao } from './dados';
import {
  ROTULO_DA_SITUACAO,
  VARIANTE_DA_SITUACAO,
  paginaDoConjunto,
  resumoDaCarga,
  rotuloDoTipo,
} from './rotulos';

const HUB = '/admin/parametros/cadastros';
const FILA = '/admin/parametros/cadastros/grafias-veiculo';

const emLista = (valores: string[]): string => (valores.length ? valores.join(', ') : 'Todos');
const linhas = (texto: string): string[] =>
  texto
    .split(/\r?\n|,/)
    .map((v) => v.trim())
    .filter(Boolean);

/**
 * Sincronização com a fonte oficial: as fontes (com o recorte, que a
 * administração ajusta), o botão "Atualizar agora" e as últimas execuções da
 * rotina, com o arquivo lido e o que cada uma fez. Enquanto há execução pedida
 * ou em andamento, a tela se atualiza sozinha.
 */
export function SincronizacaoPage() {
  const painel = usePainelDaSincronizacao();
  const [editando, setEditando] = useState<FonteDoPainel | null>(null);

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={HUB}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Cadastros &amp; Domínios
        </Link>
      </div>

      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight">Sincronização com a Fonte Oficial</h1>
          <p className="text-sm text-muted-foreground">
            Consulta diária de marcas e modelos de veículo na fonte oficial, com só as diferenças
            aplicadas ao catálogo.
          </p>
        </div>
        <ComoFuncionaAIntegracao />
      </header>

      {painel.isLoading && (
        <Card>
          <CardContent className="flex items-center justify-center gap-3 py-16">
            <Spinner />
            <p className="text-sm text-muted-foreground">Carregando a sincronização…</p>
          </CardContent>
        </Card>
      )}

      {painel.isError && !painel.data && (
        <ErrorState
          variant="load-failed"
          title="Não foi possível carregar"
          description={mensagemDoErro(
            painel.error,
            'Tente de novo. Se continuar, avise a administração do sistema.',
          )}
          primaryAction={{ label: 'Tentar de novo', onClick: () => void painel.refetch() }}
          secondaryAction={{ label: 'Cadastros & Domínios', to: HUB, variant: 'outline' }}
        />
      )}

      {painel.data && (
        <>
          {painel.data.fontes.length === 0 ? (
            <EmptyState
              title="Nenhuma fonte cadastrada"
              description="Sem fonte oficial, não há consulta diária. A fonte entra pela migração do banco."
            />
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              {painel.data.fontes.map((fonte) => (
                <CartaoDaFonte
                  key={fonte.codigo}
                  fonte={fonte}
                  emAndamento={painel.data.cargas.some(
                    (c) =>
                      c.fonte === fonte.codigo &&
                      (c.situacao === 'PEDIDA' || c.situacao === 'EM_ANDAMENTO'),
                  )}
                  aoEditar={() => setEditando(fonte)}
                />
              ))}
            </div>
          )}

          <section aria-labelledby="titulo-execucoes" className="space-y-3">
            <h2 id="titulo-execucoes" className="text-lg font-semibold">
              Últimas execuções
            </h2>
            {painel.data.cargas.length === 0 ? (
              <EmptyState
                title="Nenhuma execução ainda"
                description="A primeira consulta acontece às 5h, ou agora, pelo botão Atualizar agora."
              />
            ) : (
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableCaption className="sr-only">
                        Últimas execuções da sincronização com a fonte oficial
                      </TableCaption>
                      <TableHeader>
                        <TableRow>
                          <TableHead scope="col">Pedida em</TableHead>
                          <TableHead scope="col">Quem disparou</TableHead>
                          <TableHead scope="col">Situação</TableHead>
                          <TableHead scope="col">Arquivo da fonte</TableHead>
                          <TableHead scope="col">Resultado</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {painel.data.cargas.map((carga) => (
                          <TableRow key={carga.id}>
                            <TableCell className="whitespace-nowrap">
                              {formatarDataHora(carga.pedidaEm)}
                            </TableCell>
                            <TableCell>
                              {carga.disparo === 'MANUAL'
                                ? 'Atualizar agora (administração)'
                                : 'Consulta diária automática'}
                            </TableCell>
                            <TableCell>
                              <Badge variant={VARIANTE_DA_SITUACAO[carga.situacao]}>
                                {ROTULO_DA_SITUACAO[carga.situacao]}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {carga.recursoNome ? (
                                <span className="block">
                                  {carga.recursoNome}
                                  {carga.referencia && (
                                    <span className="block text-xs text-muted-foreground">
                                      Referência {formatarData(carga.referencia)}
                                      {carga.sha256 && ` · resumo ${carga.sha256.slice(0, 12)}`}
                                    </span>
                                  )}
                                </span>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell className="min-w-64 text-sm">
                              {resumoDaCarga(carga)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}
          </section>
        </>
      )}

      {editando && (
        <RecorteDaFonteDialogo
          key={editando.versao}
          fonte={editando}
          aoFechar={() => setEditando(null)}
        />
      )}
    </div>
  );
}

function CartaoDaFonte({
  fonte,
  emAndamento,
  aoEditar,
}: {
  fonte: FonteDoPainel;
  emAndamento: boolean;
  aoEditar: () => void;
}) {
  const atualizar = useAtualizarAgora();

  function atualizarAgora() {
    atualizar.mutate(fonte.codigo, {
      onSuccess: (pedido) => {
        if (pedido.modo === 'fila') {
          toast.warning(
            'O pedido foi registrado, mas a consulta não conseguiu começar agora. Tente de novo em alguns minutos; a consulta automática diária continua valendo.',
          );
        } else {
          toast.success('Consulta pedida. Acompanhe o resultado em Últimas execuções.');
        }
      },
      onError: (erro) =>
        toast.error(mensagemDoErro(erro, 'Não foi possível pedir a consulta. Tente de novo.')),
    });
  }

  return (
    <Card>
      <CardHeader className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="text-base">{fonte.nome}</CardTitle>
          <Badge variant={fonte.ativo ? 'default' : 'secondary'}>
            {fonte.ativo ? 'Consulta diária ligada' : 'Consulta desligada'}
          </Badge>
        </div>
        <CardDescription>
          Licença {fonte.licenca}.{' '}
          <a
            href={paginaDoConjunto(fonte.endereco)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 font-medium underline"
          >
            Conjunto de dados
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
            <span className="sr-only">(abre em outra aba)</span>
          </a>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="grid gap-2 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs text-muted-foreground">Municípios</dt>
            <dd>{emLista(fonte.municipios)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Tipos de veículo</dt>
            <dd>{emLista(fonte.tiposVeiculo.map(rotuloDoTipo))}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Espécies</dt>
            <dd>{emLista(fonte.especies)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Mínimo de veículos por texto</dt>
            <dd>{fonte.quantidadeMinima}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Textos da fonte conhecidos</dt>
            <dd>{fonte.textos.toLocaleString('pt-BR')}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Na fila de grafias</dt>
            <dd>
              {fonte.naFila > 0 ? (
                <Link to={FILA} className="font-medium text-primary underline">
                  {fonte.naFila.toLocaleString('pt-BR')} para conferir
                </Link>
              ) : (
                'Nenhum'
              )}
            </dd>
          </div>
        </dl>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            type="button"
            onClick={atualizarAgora}
            disabled={!fonte.ativo || emAndamento || atualizar.isPending}
            aria-busy={atualizar.isPending || emAndamento}
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${emAndamento ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
            {emAndamento ? 'Consulta em andamento…' : 'Atualizar agora'}
          </Button>
          <Button type="button" variant="outline" onClick={aoEditar}>
            Editar recorte
          </Button>
        </div>
        {!fonte.ativo && (
          <p className="text-xs text-muted-foreground">
            Com a consulta desligada, nem a rotina diária nem o botão consultam esta fonte. Ligue em
            Editar recorte.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function RecorteDaFonteDialogo({
  fonte,
  aoFechar,
}: {
  fonte: FonteDoPainel;
  aoFechar: () => void;
}) {
  const alterar = useAlterarRecorte();
  const [ativo, setAtivo] = useState(fonte.ativo);
  const [municipios, setMunicipios] = useState(fonte.municipios.join('\n'));
  const [tipos, setTipos] = useState(fonte.tiposVeiculo.join('\n'));
  const [especies, setEspecies] = useState(fonte.especies.join('\n'));
  const [minimo, setMinimo] = useState(String(fonte.quantidadeMinima));
  const [problema, setProblema] = useState<string | null>(null);

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const quantidade = Number(minimo);
    if (!/^\d+$/.test(minimo.trim()) || quantidade < 1) {
      setProblema('Mínimo de veículos por texto deve ser um número inteiro, 1 ou mais.');
      return;
    }
    setProblema(null);
    alterar.mutate(
      {
        fonte: fonte.codigo,
        recorte: {
          ativo,
          municipios: linhas(municipios),
          tiposVeiculo: linhas(tipos),
          especies: linhas(especies),
          quantidadeMinima: quantidade,
          versao: fonte.versao,
        },
      },
      {
        onSuccess: () => {
          toast.success(
            'Recorte salvo. A próxima consulta lê o arquivo de novo com este recorte; para aplicar já, clique em Atualizar agora.',
          );
          aoFechar();
        },
        onError: (erro) =>
          setProblema(mensagemDoErro(erro, 'Não foi possível salvar. Tente de novo.')),
      },
    );
  }

  return (
    <ResponsiveModal
      open
      onOpenChange={(aberto) => !aberto && aoFechar()}
      title="Editar recorte da fonte"
      description="O que a consulta considera no arquivo da fonte. Deixe uma lista vazia para não recortar por ela."
      desktopMaxWidth="max-w-xl"
      footer={
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={aoFechar} disabled={alterar.isPending}>
            Cancelar
          </Button>
          <Button
            type="submit"
            form="formulario-recorte"
            disabled={alterar.isPending}
            aria-busy={alterar.isPending}
          >
            {alterar.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      }
    >
      <form id="formulario-recorte" noValidate onSubmit={enviar} className="space-y-5">
        {problema && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{problema}</AlertDescription>
          </Alert>
        )}
        <div className="flex items-center gap-2">
          <Checkbox
            id="recorte-ativo"
            checked={ativo}
            onCheckedChange={(marcado) => setAtivo(marcado === true)}
          />
          <Label htmlFor="recorte-ativo" className="font-normal">
            Consultar esta fonte todo dia
          </Label>
        </div>
        <CampoDeLista
          id="recorte-municipios"
          rotulo="Municípios"
          ajuda="Um por linha, como o arquivo escreve (por exemplo, SAO PAULO)."
          valor={municipios}
          aoMudar={setMunicipios}
        />
        <CampoDeLista
          id="recorte-tipos"
          rotulo="Tipos de veículo"
          ajuda="Um por linha, como o registro do veículo escreve (por exemplo, MICROONIBUS, ONIBUS)."
          valor={tipos}
          aoMudar={setTipos}
        />
        <CampoDeLista
          id="recorte-especies"
          rotulo="Espécies"
          ajuda="Um por linha (por exemplo, PASSAGEIRO)."
          valor={especies}
          aoMudar={setEspecies}
        />
        <div className="space-y-1.5">
          <Label htmlFor="recorte-minimo">Mínimo de veículos por texto</Label>
          <Input
            id="recorte-minimo"
            inputMode="numeric"
            value={minimo}
            onChange={(e) => setMinimo(e.target.value)}
            aria-describedby="recorte-minimo-ajuda"
          />
          <p id="recorte-minimo-ajuda" className="text-xs text-muted-foreground">
            Texto com menos veículos que isso na fonte não entra (evita grafia de um veículo só).
          </p>
        </div>
      </form>
    </ResponsiveModal>
  );
}

function CampoDeLista({
  id,
  rotulo,
  ajuda,
  valor,
  aoMudar,
}: {
  id: string;
  rotulo: string;
  ajuda: string;
  valor: string;
  aoMudar: (v: string) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{rotulo}</Label>
      <Textarea
        id={id}
        rows={3}
        value={valor}
        onChange={(e) => aoMudar(e.target.value)}
        aria-describedby={`${id}-ajuda`}
      />
      <p id={`${id}-ajuda`} className="text-xs text-muted-foreground">
        {ajuda}
      </p>
    </div>
  );
}

export default SincronizacaoPage;
